import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddShipmentModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    vesselName: '',
    supplier: '',
    buyer: '',
    tonnage: '',
    coalGrade: '',
    laycanFrom: '',
    laycanTo: '',
    etd: '',
    eta: '',
    pricingWindow: 'M+1 Average',
    lcStatus: 'pending',
    status: 'contract-signed',
    contractRef: '',
    incoterm: 'FOB Newcastle',
    port: '',
    basePrice: '',
    discount: '',
    plattsIndex: 'Newcastle 6000 NAR'
  });

  const supplierOptions = [
    { value: 'whitehaven', label: 'Whitehaven Coal' },
    { value: 'yancoal', label: 'Yancoal Australia' },
    { value: 'glencore', label: 'Glencore Coal Assets' },
    { value: 'peabody', label: 'Peabody Energy Australia' }
  ];

  const buyerOptions = [
    { value: 'adani', label: 'Adani Power Limited' },
    { value: 'ntpc', label: 'NTPC Limited' },
    { value: 'tata', label: 'Tata Power Company' },
    { value: 'jsw', label: 'JSW Energy Limited' }
  ];

  const coalGradeOptions = [
    { value: 'NAR 5800', label: 'NAR 5800' },
    { value: 'NAR 5900', label: 'NAR 5900' },
    { value: 'NAR 6000', label: 'NAR 6000' },
    { value: 'NAR 6100', label: 'NAR 6100' },
    { value: 'NAR 6200', label: 'NAR 6200' },
    { value: 'NAR 6300', label: 'NAR 6300' },
    { value: 'NAR 6400', label: 'NAR 6400' },
    { value: 'NAR 6500', label: 'NAR 6500' }
  ];

  const pricingWindowOptions = [
    { value: 'M+1 Average', label: 'M+1 Average' },
    { value: 'M+2 Average', label: 'M+2 Average' },
    { value: 'Fixed Price', label: 'Fixed Price' }
  ];

  const lcStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'received', label: 'Received' },
    { value: 'expired', label: 'Expired' }
  ];

  const shipmentStatusOptions = [
    { value: 'contract-signed', label: 'Contract Signed' },
    { value: 'lc-received', label: 'LC Received' },
    { value: 'loading', label: 'Loading' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'discharged', label: 'Discharged' },
    { value: 'completed', label: 'Completed' }
  ];

  const incotermOptions = [
    { value: 'FOB Newcastle', label: 'FOB Newcastle' },
    { value: 'FOB Gladstone', label: 'FOB Gladstone' },
    { value: 'FOB Hay Point', label: 'FOB Hay Point' },
    { value: 'CFR India', label: 'CFR India' }
  ];

  const portOptions = [
    { value: 'Port of Newcastle', label: 'Port of Newcastle' },
    { value: 'Port of Gladstone', label: 'Port of Gladstone' },
    { value: 'Hay Point', label: 'Hay Point' },
    { value: 'Dalrymple Bay', label: 'Dalrymple Bay' }
  ];

  const plattsIndexOptions = [
    { value: 'Newcastle 6000 NAR', label: 'Newcastle 6000 NAR' },
    { value: 'Richards Bay 6000 NAR', label: 'Richards Bay 6000 NAR' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="Plus" size={24} />
            Add New Shipment
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 pb-8 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Vessel Name"
                type="text"
                required
                placeholder="Enter vessel name"
                value={formData?.vesselName}
                onChange={(e) => setFormData({ ...formData, vesselName: e?.target?.value })}
              />

              <Input
                label="Contract Reference"
                type="text"
                required
                placeholder="e.g., WH-AP-2025-001"
                value={formData?.contractRef}
                onChange={(e) => setFormData({ ...formData, contractRef: e?.target?.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Supplier"
                required
                options={supplierOptions}
                value={formData?.supplier}
                onChange={(value) => setFormData({ ...formData, supplier: value })}
                searchable
              />

              <Select
                label="Buyer"
                required
                options={buyerOptions}
                value={formData?.buyer}
                onChange={(value) => setFormData({ ...formData, buyer: value })}
                searchable
              />
            </div>
          </div>

          {/* Cargo Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Cargo Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Tonnage (MT)"
                type="number"
                required
                placeholder="Enter tonnage"
                value={formData?.tonnage}
                onChange={(e) => setFormData({ ...formData, tonnage: e?.target?.value })}
              />

              <Select
                label="Coal Grade"
                required
                options={coalGradeOptions}
                value={formData?.coalGrade}
                onChange={(value) => setFormData({ ...formData, coalGrade: value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Incoterm"
                required
                options={incotermOptions}
                value={formData?.incoterm}
                onChange={(value) => setFormData({ ...formData, incoterm: value })}
              />

              <Select
                label="Port"
                required
                options={portOptions}
                value={formData?.port}
                onChange={(value) => setFormData({ ...formData, port: value })}
              />
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Laycan From"
                type="date"
                required
                value={formData?.laycanFrom}
                onChange={(e) => setFormData({ ...formData, laycanFrom: e?.target?.value })}
              />

              <Input
                label="Laycan To"
                type="date"
                required
                value={formData?.laycanTo}
                onChange={(e) => setFormData({ ...formData, laycanTo: e?.target?.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="ETD (Estimated Time of Departure)"
                type="date"
                value={formData?.etd}
                onChange={(e) => setFormData({ ...formData, etd: e?.target?.value })}
              />

              <Input
                label="ETA (Estimated Time of Arrival)"
                type="date"
                value={formData?.eta}
                onChange={(e) => setFormData({ ...formData, eta: e?.target?.value })}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Pricing</h3>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Base Price ($/MT)"
                type="number"
                step="0.01"
                required
                placeholder="0.00"
                value={formData?.basePrice}
                onChange={(e) => setFormData({ ...formData, basePrice: e?.target?.value })}
              />

              <Input
                label="Discount ($/MT)"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData?.discount}
                onChange={(e) => setFormData({ ...formData, discount: e?.target?.value })}
              />

              <Select
                label="Pricing Window"
                required
                options={pricingWindowOptions}
                value={formData?.pricingWindow}
                onChange={(value) => setFormData({ ...formData, pricingWindow: value })}
              />
            </div>

            <Select
              label="Platts Index"
              required
              options={plattsIndexOptions}
              value={formData?.plattsIndex}
              onChange={(value) => setFormData({ ...formData, plattsIndex: value })}
            />
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Shipment Status"
                required
                options={shipmentStatusOptions}
                value={formData?.status}
                onChange={(value) => setFormData({ ...formData, status: value })}
              />

              <Select
                label="LC Status"
                required
                options={lcStatusOptions}
                value={formData?.lcStatus}
                onChange={(value) => setFormData({ ...formData, lcStatus: value })}
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit} iconName="Save" iconPosition="left">
            Save Shipment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddShipmentModal;